import React, { useState, useEffect, useRef } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "universal-cookie";
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Nav } from "@/_components/navigation/Nav";
import { langConversionHelper } from "@/_helpers";
import { setReferralSite } from "../_redux/actions/referralActions";
import moment from "moment-timezone";
import favIconLogo from "../_images/favicons/ls_favicon.png";
import favIconMonLogo from "../_images/favicons/mon_favicon.png";
import favIconGuardLogo from "../_images/favicons/guard_favicon.png";
import favIconQsLogo from "../_images/favicons/qs_favicon.png";
import { brandColors } from "../_components/brandedComponent/BrandedColors";
// import { useToaster } from "../_components/toaster/MuiToasterProvider";
import {
  LifeSafer,
  Monitech,
  Guardian,
  QuickStart,
} from "../_helpers/BrandConstants";
import { validateEnrollmentCode } from "../_api/enrolleeApi";
import Entry from "../pages/Entry";
import MyAccount from "../pages/MyAccount";
import WelcomeBack from "../pages/WelcomeBack";
import PickAStory from "../pages/PickAStory";
import Story from "../pages/story/storyComponents/Story";
import ContactPage from "../pages/ContactPage";
import ContactInfo from "../_components/steps/ContactInfo";
import AddressInfo from "../_components/steps/AddressInfo";
import IdInfo from "../_components/steps/IdInfo";
import LppInfo from "../_components/steps/LppInfo";
import ScheduleInfo from "../_components/steps/ScheduleInfo";
import VehicleInfos from "../_components/steps/VehicleInfos";
import ViolationInfo from "../_components/steps/ViolationInfo";
import VerifySecurityCode from "../pages/VerifySecurityCode";
import LearnMore from "../_components/steps/LearnMore";
import Driver from "../_components/steps/Driver";
import Pricing from "../_components/steps/Pricing";
import WhyIsa from "../_components/steps/WhyIsa";
import ReadySpeed from "../_components/steps/ReadySpeed";
import { useDeviceTypeWatcher } from "@/_helpers/PhoneNumberHelper";
import PickBackUpEnrollment from "../pickUp/PickBackUpEnrollment";

const theme = createTheme({
  palette: {
    primary: {
      light: "#495d7e",
      main: "#1c355e",
      dark: "#132541",
      contrastText: "#fff",
    },
    secondary: {
      light: "#ffffff",
      main: "#ffffff",
      dark: "#ffffff",
      contrastText: "#1c355e",
    },
  },
  typography: {
    h6: {
      fontSize: "1.1rem",
      "@media (max-width:960px)": {
        fontSize: ".97rem",
      },
    },
  },
  components: {
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: "#76bc21",
          boxShadow: "0 1px 4px rgba(0, 0, 0, 0.3)",
          "&.Mui-disabled": {
            boxShadow: "none",
          },
          "&.Mui-selected": {
            backgroundColor: "#76bc21",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#76bc21",
              color: "#fff",
            },
          },
        },
      },
    },
  },
});

function App() {
  const cookies = new Cookies();
  const { pathname } = useLocation();
  const [selected, setSelected] = useState();
  const { i18n } = useTranslation();
  const [referralSiteData, setReferralSiteData] = useState(null);
  const isInitialLoad = useRef(false);
  const cookiesInstance = new Cookies();
  const user = cookiesInstance.get("EnrollNow") || {};
  const { phoneNumber, resolvedUrl, siteLogo, isISA } = useDeviceTypeWatcher();
  // const toast = useToaster();
  const isMountedRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (!isInitialLoad.current) {
      const supportedLangs = langConversionHelper.languageList();
      const enrollmentCode = cookiesInstance.get("lsEnrollmentCode") || {};
      const cookieLanguage =
        user?.LanguageId ??
        user?.languageId ??
        user?.LanguageID ??
        user?.languageID ??
        user?.Language ??
        user?.language;
      const enrollmentCodeLanguage =
        enrollmentCode?.languageId ??
        enrollmentCode?.LanguageId ??
        enrollmentCode?.languageID ??
        enrollmentCode?.LanguageID;
      const langToSet = [cookieLanguage, enrollmentCodeLanguage]
        .map((preferredLanguage) => {
          const formattedLang = String(
            preferredLanguage ?? "",
          ).toLowerCase();
          const matchedLang = supportedLangs.find(
            (x) =>
              x.langAbbrev.toLowerCase() === formattedLang ||
              x.nativeName.toLowerCase() === formattedLang ||
              x.displayName.toLowerCase() === formattedLang ||
              String(x.languageId ?? x.languageID) === formattedLang,
          );
          const matchedLanguageCode =
            (matchedLang?.languageId ?? matchedLang?.languageID) === 1
              ? "enUS"
              : matchedLang?.langAbbrev;
          return matchedLanguageCode &&
            i18n.hasResourceBundle(matchedLanguageCode, "translation")
            ? matchedLanguageCode
            : null;
        })
        .find(Boolean) || "enUS";
      i18n.changeLanguage(langToSet);
      localStorage.setItem("lang", langToSet);
      isInitialLoad.current = true;
    }
  }, []);

  //Overall Script loaded of google map api to resolve conflict between google map call - Ankit
  useEffect(() => {
    isMountedRef.current = true;
    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com/maps/api/js"]`,
    );

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_KEY}&libraries=places,marker&loading=async`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        if (isMountedRef.current) {
          setIsLoaded(true);
        }
      };
      script.onerror = () => {
        if (isMountedRef.current) {
          console.error("Failed to load Google Maps API");
        }
      };

      document.head.appendChild(script);
    } else {
      setIsLoaded(true);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
  let isMounted = true;

  const ensureEnrollmentCode = async () => {
    const isSpeedStory = pathname
      ?.toLowerCase()
      .startsWith("/story/speed");

    const codeFromCookie = cookiesInstance.get("lsEnrollmentCode");

    const shouldReplaceWithIsaDefault =
      isSpeedStory &&
      codeFromCookie?.enrollmentCode === "LIFESAFERDEFAULT";

    // Existing valid code can be used unless Speed story
    // specifically needs LIFESAFERDEFAULTISA instead.
    if (
      codeFromCookie?.enrollmentCodeId &&
      !shouldReplaceWithIsaDefault
    ) {
      return;
    }

    const isStoryRoute = pathname
      ?.toLowerCase()
      .startsWith("/story/");
    if (!isStoryRoute) return;

    try {
      const brandIdentifier =
        cookiesInstance.get("lsEnrollmentReferral") || LifeSafer;

      const defaultEnrollmentCodeName = shouldReplaceWithIsaDefault
        ? "LIFESAFERDEFAULTISA"
        : "LIFESAFERDEFAULT";

      const defaultEnrollmentCode = await validateEnrollmentCode(
        defaultEnrollmentCodeName,
        brandIdentifier
      );

      if (
        isMounted &&
        defaultEnrollmentCode?.enrollmentCodeId
      ) {
        cookiesInstance.set(
          "lsEnrollmentCode",
          defaultEnrollmentCode,
          {
            path: "/",
            sameSite: "strict",
          }
        );

        return;
      }
    } catch (error) {
      console.error(
        "Default enrollment code bootstrap failed:",
        error
      );
    }

    if (isMounted) {
      navigate("/pick-a-story");
    }
  };

  ensureEnrollmentCode();

  return () => {
    isMounted = false;
  };
}, [navigate, pathname]);

  function setTheme(themeToSet) {
    theme.palette.primary.light = brandColors[themeToSet].primaryLight;
    theme.palette.primary.main = brandColors[themeToSet].primary;
    theme.palette.primary.dark = brandColors[themeToSet].primaryDark;
    theme.palette.secondary.contrastText = brandColors[themeToSet].primary;
  }

  function setReferringSite(referringSite = LifeSafer) {
    referringSite = referringSite?.toLowerCase();
    setReferralSiteData(referringSite);
    const isLifeSafer = referringSite?.includes(LifeSafer);
    const isMonitech = referringSite?.includes(Monitech);
    const isGuardian = referringSite?.includes(Guardian);
    const isQuickStart = referringSite?.includes(QuickStart);

    cookies.remove("lsEnrollmentReferral");
    cookies.remove("lsBackgroundImage");
    let faviconElement = document.getElementById("favicon");
    if (isLifeSafer) {
      setReferralSite(LifeSafer);
      setTheme(LifeSafer);
      faviconElement.href = favIconLogo;
      cookies.set("lsEnrollmentReferral", LifeSafer, {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(24, "h").format()),
      });
      // added new Code -akluitel
      cookies.set("lsBackgroundImage", ``, {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(24, "h").format()),
      });
      document.title = "LifeSafer - Enrollment";
    } else if (isMonitech) {
      setReferralSite(Monitech);
      setTheme(Monitech);
      faviconElement.href = favIconMonLogo;
      cookies.set("lsEnrollmentReferral", Monitech, {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(24, "h").format()),
      });
      cookies.set("lsBackgroundImage", ``, {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(24, "h").format()),
      });
      document.title = "Monitech - Enrollment";
    } else if (isGuardian) {
      setReferralSite(Guardian);
      setTheme(Guardian);
      faviconElement.href = favIconGuardLogo;
      cookies.set("lsEnrollmentReferral", Guardian, {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(24, "h").format()),
      });
      cookies.set("lsBackgroundImage", ``, {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(24, "h").format()),
      });
      document.title = "Guardian - Enrollment";
    } else if (isQuickStart) {
      setReferralSite(QuickStart);
      setTheme(QuickStart);
      faviconElement.href = favIconQsLogo;
      cookies.set("lsEnrollmentReferral", QuickStart, {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(24, "h").format()),
      });
      cookies.set("lsBackgroundImage", ``, {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(24, "h").format()),
      });
      document.title = "QuickStart - Enrollment";
    } else if (!isLifeSafer && !isMonitech && !isGuardian && !isQuickStart) {
      ////Test localhost with LS
      setReferralSite(LifeSafer);
      setTheme(LifeSafer);
      faviconElement.href = favIconLogo;
      cookies.set("lsEnrollmentReferral", LifeSafer, {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(24, "h").format()),
      });
      cookies.set("lsBackgroundImage", ``, {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(24, "h").format()),
      });
      document.title = "LifeSafer - Enrollment";
    }
  }

  useEffect(() => {
    setReferringSite(user?.URL || "");
    if (pathname.slice(1, 6) === "intro") {
      //set language
      let lang = "enUS";
      const urlParams = new URLSearchParams(window.location.search);
      const langParameter = urlParams.get("lang");
      if (langParameter !== undefined && langParameter !== null) {
        lang = urlParams.get("lang");
      } else if (pathname.length === 3) {
        //Get desired language
        lang = pathname.slice(1, 3).toLowerCase();
      }

      if (pathname.length > 6) {
        let signifier = pathname.split("/").pop();
        if (
          (langParameter === undefined || langParameter === null) &&
          signifier.length === 2
        ) {
          lang = signifier;
        } else {
          localStorage.setItem("page", "Enrollment Code");
          cookies.set("lsEnrollmentCode", signifier, {
            path: "/",
            sameSite: "strict",
            expires: new Date(moment().add(2, "m").format()),
          });
        }
      }
      //Check to make sure we support that language
      let lngs = langConversionHelper.languageList();
      lang === "en" ? "en-US" : lang;
      const findLang = lngs.filter((x) => x.langAbbrev === lang);
      //Default to English if we don't support
      if (findLang.length === 0) lang = "en-US";
      localStorage.setItem("lang", lang);
      // i18n.changeLanguage(lang);
    }

    if (pathname.slice(1, 6) === "entry" && pathname.length > 6) {
      // Does entry have a signifier?
      let entrySignifier = pathname.split("/").pop(); //pathname.substr(pathname.lastIndexOf("/") + 1); // pathname.split("/").pop();
      if (entrySignifier.toUpperCase() === "TEST") {
        cookies.set("lsEnrollmentEntryType", "Test", {
          path: "/",
          sameSite: "strict",
          expires: new Date(moment().add(20, "m").format()),
        });
      } else if (entrySignifier.toUpperCase() === "MAILER") {
        cookies.set("lsEnrollmentEntryType", "Mailer", {
          path: "/",
          sameSite: "strict",
          expires: new Date(moment().add(20, "m").format()),
        });
      } else if (entrySignifier.toUpperCase() === "DUISOLUTIONS") {
        cookies.set("lsEnrollmentEntryType", "DUISolutions", {
          path: "/",
          sameSite: "strict",
          expires: new Date(moment().add(20, "m").format()),
        });
      } else {
        cookies.remove("lsEnrollmentEntryType", { path: "/" });
      }
    } else {
      cookies.remove("lsEnrollmentEntryType", { path: "/" });
    }
  }, []);

  useEffect(() => {
    let lang = "en-US";
    if (pathname.length === 3) {
      lang = pathname.slice(1, 3);
      const lngs = langConversionHelper.languageList();
      lang = lang === "en" ? "en-US" : lang;
      const findLang = lngs.find((x) => x.langAbbrev === lang);
      if (!findLang) lang = "en-US";
    }
    localStorage.setItem("LSLanguageCode", lang);
  }, []);

  useEffect(() => {
    setCountryCode(window.location);
    setSelected(window.location.pathname);
  }, [pathname]);

  useEffect(() => {
    if (pathname !== "/admin") {
      localStorage.removeItem("locationManagerBrand");
      localStorage.removeItem("locationManagerCountry");
      localStorage.removeItem("locationManagerState");
      localStorage.removeItem("locationManagerLocation");
    }
  }, [pathname]);

  const setCountryCode = (location) => {
    // let defaultLang = "en-US";
    const hostname = location.hostname.split(".");
    if (hostname[hostname.length - 1] === "au") {
      sessionStorage.setItem("LSCountryCode", "AU");
    } else if (
      hostname[hostname.length - 1] === "eu" ||
      String(process.env.REACT_APP_ENV || "").toLowerCase().startsWith("eu")
    ) {
      sessionStorage.setItem("LSCountryCode", "EU");
    } else {
      sessionStorage.setItem("LSCountryCode", "US");
    }
    // if (i18n.language !== defaultLang) {
    //   i18n.changeLanguage(defaultLang);
    // }
  };

  const handleNavUpdate = (index) => {
    setSelected(index);
  };

  function TrailingSlashRedirect() {
    const location = useLocation();
    const { pathname, search, hash } = location;

    // Ignoring root '/' and paths without trailing slash
    if (pathname.length > 1 && pathname.endsWith("/")) {
      // Removing trailing slash
      const newPath = pathname.slice(0, -1);
      return <Navigate to={newPath + search + hash} replace />;
    }

    return null; // no redirecting if no trailing slash
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <TrailingSlashRedirect />
        <div style={{ display: "flex" }}>
          <Nav
            referralSiteData={referralSiteData}
            selectedNav={selected}
            updateSelected={handleNavUpdate}
            phoneNumber={phoneNumber}
            resolvedUrl={resolvedUrl}
            siteLogo={siteLogo}
            isISA={isISA}
          />
          <Routes>
            <Route
              path="/continue/:token"
              suspense={true}
              element={<PickBackUpEnrollment />}
            />
            <Route
              path="/go/:tokenId"
              suspense={true}
              element={<PickBackUpEnrollment />}
            />
            <Route path="/*" suspense={true} element={<Entry />} />
            <Route
              path="/story/"
              suspense={true}
              caseSensitive={false}
              element={<Entry />}
            />
            {/* <Route path="/refer/:EnrollmentCode" suspense={true} element={<Entry />} /> */}
            <Route path="/entry" suspense={true} element={<Entry />} />
            <Route
              path="/entry/:enrollmentCodeParam?"
              suspense={true}
              element={<Entry />}
            />
            <Route path="/my-account" suspense={true} element={<MyAccount />} />
            <Route
              path="/welcome-back"
              suspense={true}
              element={<WelcomeBack phoneNumber={phoneNumber} />}
            />
            <Route
              path="/contact-page"
              suspense={true}
              element={<ContactPage phoneNumber={phoneNumber} />}
            />
            <Route
              path="/pick-a-story"
              suspense={true}
              element={<PickAStory />}
            />
            <Route
              path="/verify"
              suspense={true}
              element={<VerifySecurityCode phoneNumber={phoneNumber} />}
            />
            <Route path="/contact-info" element={<ContactInfo />} />
            <Route path="/address-info" element={<AddressInfo />} />
            <Route path="/id-info" element={<IdInfo />} />
            <Route path="/lpp-info" element={<LppInfo />} />
            <Route path="/schedule-info" element={<ScheduleInfo />} />
            <Route path="/learn-more" element={<LearnMore />} />
            <Route path="/driver" element={<Driver />} />
            <Route path="/pricing-info" element={<Pricing />} />
            <Route path="/why-isa" element={<WhyIsa />} />
            <Route path="/ready-speed" element={<ReadySpeed />} />
            <Route path="/vehicle-info" element={<VehicleInfos />} />
            <Route path="/violation-info" element={<ViolationInfo />} />
            <Route
              path="/story/:storyName?"
              suspense={true}
              caseSensitive={false}
              element={
                <Story
                  referralSiteData={referralSiteData}
                  phoneNumber={phoneNumber}
                  isLoaded={isLoaded}
                />
              }
            />
          </Routes>
        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export { App };
